import { AssetAllocation } from './lti/investment-portfolio.domain';
import { Currency } from './market.domain';

export type SingleAccountGroup = '$account';

export enum PerformanceGroup {
  DEFAULT = '$account'
}

export enum AccountStatus {
  NOT_AVAILABLE = 'NOT_AVAILABLE',
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
  FROZEN = 'FROZEN'
}

export enum AccountDivisionType {
  UNKNOWN = 'UNKNOWN',
  RAPID_MARKET_ACCESS = 'RAPID_MARKET_ACCESS',
  PARTNER_SERVICES = 'PARTNER_SERVICES',
  TD_ASSET_MANAGEMENT = 'TD_ASSET_MANAGEMENT',
  FINANCIAL_PLANNERS = 'FINANCIAL_PLANNERS',
  INSTITUTIONAL_SERVICES = 'INSTITUTIONAL_SERVICES',
  PRIVATE_CLIENT = 'PRIVATE_CLIENT',
  PRIVATE_INVESTMENT_COUNSEL = 'PRIVATE_INVESTMENT_COUNSEL'
}

export enum AccountPlatform {
  DIRECT_INVESTMENT = 'DIRECT_INVESTMENT',  // RAPID_MARKET_ACCESS
  DIRECT_INVESTMENT_LITE = 'DIRECT_INVESTMENT_LITE', // DIRECT_INVESTMENT for Easy Trade
  DIRECT_INVESTMENT_THINKORSWIM = 'DIRECT_INVESTMENT_THINKORSWIM', // DIRECT_INVESTMENT with TradingPlatform TOS
  DIRECT_INVESTMENT_ACTIVE_TRADER = 'DIRECT_INVESTMENT_ACTIVE_TRADER', // Shouldn't have, were converted to DI accounts

  FINANCIAL_PLANNING = 'FINANCIAL_PLANNING', // FINANCIAL_PLANNERS
  PRIVATE_INVESTMENT_ADVICE = 'PRIVATE_INVESTMENT_ADVICE', // PRIVATE_CLIENT
  PRIVATE_INVESTMENT_COUNSEL = 'PRIVATE_INVESTMENT_COUNSEL', // PRIVATE_INVESTMENT_COUNSEL

  // Eventually, remove these, if not needed
  PARTNER_SERVICES = 'PARTNER_SERVICES',
  ASSET_MANAGEMENT = 'ASSET_MANAGEMENT', // TD_ASSET_MANAGEMENT
  INSTITUTIONAL_SERVICES = 'INSTITUTIONAL_SERVICES',

  UNKNOWN = 'UNKNOWN'
}

export enum GuaranteedType {
  UNKNOWN = 'UNKNOWN',
  NONE = 'NONE',
  GUARANTOR = 'GUARANTOR',
  GUARANTEE = 'GUARANTEE',
  GUARANTOR_GUARANTEE = 'GUARANTOR_GUARANTEE'
}

export enum TradingPlatform {
  ATP = 'ATP',
  TOS = 'TOS',
  WEBBROKER = 'WEBBROKER',
  MPOWER = 'MPOWER'
}

export enum BusinessUnit {
  TDW = 'TDW',
  TDCT = 'TDCT',
  OFI = 'OFI'
}

export enum CorporateOrganization {
  BROKERAGE = 'BROKERAGE',
  RETAIL = 'RETAIL'
}

export enum AccountType {
  UNKNOWN = 'UNKNOWN',
  CASH = 'CASH',
  COD = 'COD',
  MARGIN = 'MARGIN',
  MARGIN_SHORT = 'MARGIN_SHORT',
  SDRSP_INV = 'SDRSP_INV',
  MARGIN_NON_ADV = 'MARGIN_NON_ADV',
  CPRSP = 'CPRSP',
  QSSP = 'QSSP',
  SDRSP = 'SDRSP',
  SDRIF = 'SDRIF',
  RESP = 'RESP',
  INCOME = 'INCOME',
  BASIC_RSP = 'BASIC_RSP',
  BASIC_RIF = 'BASIC_RIF',
  TFSA = 'TFSA',
  RDSP = 'RDSP',
  FHSA = 'FHSA',
  OTHERS = 'OTHERS',
  SHORT = 'SHORT', // TODO see about that with Backend
  NON_SHORT = 'NON_SHORT'
}

export type CashBalanceType = number | { [currency: string]: CashBalance };

export type CurrencyValueMap = {
  [key in Currency]?: CashBalance;
};

export interface CashBalance {
  currency: Currency;
  amount: number;
}

export type AccountId = string;
export type AccountNumber = string;
export type GroupNumber = string;

// TODO: change to AccountType and drop unused properties
export interface AccountTypeDetails {
  name: string;
  code: number;
  stringValue: string;
  acctType: AccountType;
}

export interface BaseAccount {
  id: string;
  status?: AccountStatus;
  favorite: boolean;
}

export interface Account extends BaseAccount {

  number: string;
  groupId: string;
  groupNumber: string;
  type?: AccountTypeDetails;
  businessUnit?: BusinessUnit;
  description: string;
  rapCode?: RapCode; // TODO: used in cache transfers  and trading
  accountPlatform: AccountPlatform;
  currency: Currency;
  corporateOrganization?: CorporateOrganization; // Used for sorting  and transfers
  balanceDate?: Date;
  businessLineDescription?: string; // Added by account description service
  accountTypeDescription?: string; // Added by account description service
  nickName?: string;
  hasFplPairedAccount?: boolean; // Used for FPL if there is any associated lending account
}

export type SortableAccount = Pick<Account,
'number' |
'groupNumber' |
'accountPlatform' |
'favorite' |
'corporateOrganization' |
'status' |
'nickName'
>;

export type DescribableAccount = Pick<Account,
'type' |
'accountPlatform' |
'businessUnit' |
'nickName' |
'businessLineDescription' |
'accountTypeDescription'
> &  Partial<Pick<Account, 'description'>>;

export class AccountHelper {

  static isPICAccount(account: Pick<Account, 'accountPlatform'>): boolean {
    return account.accountPlatform === AccountPlatform.PRIVATE_INVESTMENT_COUNSEL;
  }

  static isDIAccount(account: Pick<Account, 'accountPlatform'>): boolean {
    return account?.accountPlatform === AccountPlatform.DIRECT_INVESTMENT;
  }

  static isDICashAccount(account: Pick<Account, 'accountPlatform' | 'type'>): boolean {
    const accountType = account?.type?.acctType;
    return account?.accountPlatform === AccountPlatform.DIRECT_INVESTMENT && accountType === AccountType.CASH;
  }

  static isMarginAccount(account: Pick<Account, 'type'>): boolean {
    const accountType = account.type.acctType;
    return accountType === AccountType.MARGIN || accountType === AccountType.MARGIN_SHORT || accountType === AccountType.MARGIN_NON_ADV;
  }

  static isMarginShort(account: Pick<Account, 'type'>): boolean {
    return account?.type.acctType === AccountType.MARGIN_SHORT;
  }

  static isRegisteredAccount(account: Pick<Account, 'type'>): boolean {
    const accountType = account?.type?.acctType;
    // TFSA('J', 'K'), RDSP('N'), SDRSP('S', 'U'), SDRIF('T' ,'I'), RESP('V'), BASIC_RSP('X'), BASIC_RIF('Y'), CPRSP('P')
    return accountType === AccountType.TFSA || accountType === AccountType.RDSP || accountType === AccountType.SDRSP ||
      accountType === AccountType.SDRIF || accountType === AccountType.RESP || accountType === AccountType.BASIC_RSP ||
      accountType === AccountType.BASIC_RIF || accountType === AccountType.CPRSP;
  }

  static isPaymentInformationAvailable(account: Pick<Account, 'type'>): boolean {
    const accountType = account?.type?.acctType;
    // RR('T', 'Y')
    return accountType === AccountType.SDRIF || accountType === AccountType.BASIC_RIF;
  }

  static isContributionInformationAvailable(account: Pick<Account, 'status' | 'type'>): boolean {
    const accountType = account.type.acctType;
    // RRSP('P', 'S', 'X' ) or RESP('V')
    const accountTypeEligible = accountType === AccountType.CPRSP || accountType === AccountType.SDRSP ||
      accountType === AccountType.BASIC_RSP || accountType === AccountType.RESP;
    const accountIsOpen = account.status !== AccountStatus.CLOSED;
    return accountTypeEligible && accountIsOpen;
  }

  static isRegisteredAccountByRapCode(account: Pick<Account, 'type' | 'rapCode'>,
                                      currency: Currency = Currency.CAD): boolean {
    if (currency === Currency.CAD) {
      return AccountHelper.rapCodeInArray(account, TRADING_ACCOUNT_DESCRIPTION.CAD_REGISTERED_ACCOUNT_RAP_CODES);
    } else if (currency === Currency.USD) {
      return AccountHelper.rapCodeInArray(account, TRADING_ACCOUNT_DESCRIPTION.USD_REGISTERED_ACCOUNT_RAP_CODES);
    }
  }

  static isShortOrMarginAccount(account: Pick<Account, 'type' | 'rapCode'>): boolean {
    return AccountHelper.rapCodeInArray(account, TRADING_ACCOUNT_DESCRIPTION.SHORT_OR_MARGIN_ACCOUNT_RAP_CODES);
  }

  static isShortAccount(account: Pick<Account, 'type' | 'rapCode'>): boolean {
    return AccountHelper.rapCodeInArray(account, TRADING_ACCOUNT_DESCRIPTION.SHORT_ACCOUNT_RAP_CODES);
  }

  static isClosed(account: Pick<Account, 'status'>): boolean {
    return account !== undefined && account.status === AccountStatus.CLOSED;
  }

  static rapCodeInArray(account: Pick<Account, 'type' | 'rapCode'>, rapCodes: Array<string>): boolean {
    let rapCode: string;
    if (account !== undefined && account.type !== undefined && account.type.stringValue !== undefined) {
      rapCode = account.type.stringValue;
    } else {
      if (account !== undefined && account.rapCode !== undefined && account.rapCode.stringValue !== undefined) {
        rapCode = account.rapCode.stringValue;
      } else {
        rapCode = undefined;
      }
    }
    return rapCode !== undefined && rapCodes.some((value: string): boolean => value === rapCode);
  }
}

export interface AccountGroupResponse {
  groupId: string;
  groupNumber: string;
  accountPlatform: AccountPlatform;
  favorite: boolean;
}

export interface AccountGroup extends AccountGroupResponse {
  businessLineDescription?: string;
  accountTypeDescription?: string;
  nickName?: string;
  // businessUnit?: BusinessUnit; // TODO: is it applicable to a group?
}

export interface AccountDetailsResponse extends Account {
  businessLine: string; // TODO vpopov need for account_v2.details' in cancel-order-form.component.ts
  balances?: Balance; // TODO: not used
  additionalDetails: {
    HOLDINGS_BALANCES?: HoldingsBalances;
    RT_CASH_DETAILS?: RealTimeCashBalance;
    HOLDINGS_ALLOCATIONS?: Array<AssetAllocation>;
    BUYING_POWER?: BuyingPower;
    KYC_PROFILE?: KYCProfile;
  };
}

export type SortableAccountGroup = Pick<AccountGroup,
'groupNumber' |
'favorite' |
'nickName' |
'accountPlatform'
>;

export interface KYCProfile {
  insider: boolean;
}

// this object is not used in the UI - should be removed from the call
export interface Balance {
  balanceDate: Date;
  realTimeAsOfDate: Date; // populated by holdings only
  bookValueAvailable: boolean; // false for details true and true when you go through holdings
  consolidatedMarginBalance: number; // getting got both account details and holdings calls
}

export interface BuyingPower {
  buyingPower: number;
  clearedFundsBuyingPower: number;
  totalCashBalance: number;
  totalMargin: number;
  totalMarketValue: number;
}

export interface RealTimeCashBalance {
  realTimeAsOfDate: Date;
  bookValueAvailable: boolean; // TODO: do we use it?
  realTimeCashBalance: number;
}

// Balances retrieved by account details with HOLDINGS_BALANCES filter and holdings call
export interface HoldingsBalances extends RealTimeCashBalance {
  balanceDate: Date;
  cashBalance: number;
  securityBalance: number;
  marginBalance: number;
  realTimeSecurityMarketValue: number;
  totalMarketValue?: number; // TODO: consolidate next two
  realTimeTotalMarketValue: number;
  totalAccountCashPercent: number;
  totalAccountSecuritiesPercent: number;
  totalAccountBookValue: number;
  totalAccountGainLossDay: number;
  totalUnrealizedGainLossDollar?: number;
  totalUnrealizedGainLossPercent?: number;
  assetAllocations?: Array<AssetAllocation>;
  accountId?: string;
}

export enum AccountDetailType {
  KYC_PROFILE = 'KYC_PROFILE',
  RT_CASH_DETAILS = 'RT_CASH_DETAILS',
  HOLDINGS_BALANCES = 'HOLDINGS_BALANCES',
  BUYING_POWER = 'BUYING_POWER',
  PERFORMANCE_DETAILS = 'PERFORMANCE_DETAILS'
}

export enum PlanCode {
  COM = 'COMPANION_SAVINGS',
  AME = 'COMPANION_SAVINGS_FR',
  FUL = 'FULL_SERVE',
  PLS = 'FULL_SERVE_FR',
  P60 = 'PLAN_60',
  PRF = 'PREFERRED_CHEQUING',
  PRV = 'PREFERRED_CHEQUING_FR',
  PRI = 'PRIVATE_BANKING',
  SBP = 'PRIVATE_BANKING_FR',
  SEL = 'SELECT_SERVICE',
  SLF = 'SELF_SERVE',
  LIB = 'SELF_SERVE_FR',
  STU = 'SELF_SERVE_STUDENT',
  ETU = 'SELF_SERVE_STUDENT_FR',
  STF = 'STAFF',
  EMP = 'STAFF_FR',
  ALL = 'TD_ALL_INCLUSIVE_BANKING_PLAN',
  CPR = 'TD_ALL_INCLUSIVE_BANKING_PLAN_FR',
  EVD = 'TD_EVERY_DAY_CHEQUING_ACCOUNT',
  QTD = 'TD_EVERY_DAY_CHEQUING_ACCOUNT_FR',
  EDS = 'TD_EVERY_DAY_SAVINGS_ACCOUNT',
  CEQ = 'TD_EVERY_DAY_SAVINGS_ACCOUNT_FR',
  HIS = 'TD_HIGH_INTEREST_SAVINGS_ACCOUNT',
  CIE = 'TD_HIGH_INTEREST_SAVINGS_ACCOUNT_FR',
  MIN = 'TD_MINIMUM_CHEQUING_ACCOUNT',
  ULT = 'TD_UNLIMITED_CHEQUING_ACCOUNT',
  ILR = 'TD_UNLIMITED_CHEQUING_ACCOUNT_FR',
  TIR = 'TIER_SAVINGS',
  PRO = 'TIER_SAVINGS_FR',
  VAL = 'VALUE',
  VPL = 'VALUE_PLUS',
  VPX = 'VALUE_PLUS_FR'
}

export interface RapCode {
  name: RapCodeType;
  code: number;
  stringValue: string;
  acctType: AccountType;
}

export enum RapCodeType {
  UNKNOWN = 'UNKNOWN',
  CDN_CASH = 'CDN_CASH',
  US_CASH = 'US_CASH',
  CDN_COD = 'CDN_COD',
  US_COD = 'US_COD',
  CDN_MARGIN = 'CDN_MARGIN',
  US_MARGIN = 'US_MARGIN',
  CDN_MARGIN_SHORT = 'CDN_MARGIN_SHORT',
  US_MARGIN_SHORT = 'US_MARGIN_SHORT',
  SDRSP_INV = 'SDRSP_INV',
  CDN_MARGIN_NON_ADV = 'CDN_MARGIN_NON_ADV',
  US_MARGIN_NON_ADV = 'US_MARGIN_NON_ADV',
  CPRSP = 'CPRSP',
  QSSP = 'QSSP',
  SDRSP = 'SDRSP',
  SDRIF = 'SDRIF',
  RESP = 'RESP',
  CDN_INCOME = 'CDN_INCOME',
  BASIC_RSP = 'BASIC_RSP',
  BASIC_RIF = 'BASIC_RIF',
  US_INCOME = 'US_INCOME',
  TFSA = 'TFSA',
  RDSP = 'RDSP',
  US_SDRSP = 'US_SDRSP',
  US_TFSA = 'US_TFSA',
  FHSA = 'FHSA'
}

export interface ContributionResponse {
  first60DaysContribution: number;
  first60DaysSpousalContribution: number;
  previousYearContribution: number;
  previousYearSpousalContribution: number;
  remainingContribution: number;
  remainingSpousalContribution: number;
}

export interface RrifPaymentResponse {
  electedPaymentAmount: number;
  minimumPaymentAmount: number;
  paymentDate: Date;
  paymentFrequency: RrifPaymentFrequency;
}

export enum RrifPaymentFrequency {
  WEEKLY = 'A',
  BIWEEKLY = 'B',
  MONTHLY = 'M',
  QUARTERLY = 'Q',
  SEMIANNUAL = 'S',
  YEARLY = 'Y'
}

// TODO: should match Account interface fields
export interface RelatedAccount {
  accountNumber: string;
  accountId: string;
}

export interface AccountPerformanceGroup {
  name: string;
  rapCodes: Array<string>;
}

export interface TradingAccount extends Account {
  shortAccount?: boolean;
  shortOrMarginAccount?: boolean;
  currentAccountDescription?: string;
  accountType?: AccountType;
  allowedActions?: Array<string>;
  optionTradeAuthorizationCode?: number; // Used for option trading
  cashBalance?: CashBalanceType;
}
export interface TradingAccountDescription {
  SHORT_ACCOUNT_RAP_CODES: Array<string>;
  SHORT_OR_MARGIN_ACCOUNT_RAP_CODES: Array<string>;
  CAD_REGISTERED_ACCOUNT_RAP_CODES: Array<string>;
  USD_REGISTERED_ACCOUNT_RAP_CODES: Array<string>;
}

export const TRADING_ACCOUNT_DESCRIPTION: TradingAccountDescription = class {
  static readonly SHORT_ACCOUNT_RAP_CODES: Array<string> = ['G', 'H'];
  static readonly SHORT_OR_MARGIN_ACCOUNT_RAP_CODES: Array<string> = ['E', 'F', 'G', 'H', 'L', 'M'];
  static readonly CAD_REGISTERED_ACCOUNT_RAP_CODES: Array<string> = ['N', 'O', 'P', 'V', 'X', 'Y'];
  static readonly USD_REGISTERED_ACCOUNT_RAP_CODES: Array<string> = ['I', 'J', 'K', 'S', 'T', 'U'];
};
